import React, { memo, useMemo } from "react";

const MAPBOX_BASE = "https://api.mapbox.com/styles/v1/mapbox/streets-v12/static";

const getTenantStaticMapColor = () => {
  if (typeof window === "undefined") return "007bff";
  const themeVars = window.__TENANT_CONFIG__?.themeVars || {};
  const hex =
    themeVars["--tenant-accent-color"] ||
    themeVars["--tenant-primary-color"] ||
    "";
  const clean = hex.replace("#", "");
  return clean.length === 6 ? clean : "007bff";
};

// Google Polyline Encoding - encodes [lng,lat] pairs into a compact ASCII string.
// Mapbox Static API accepts encoded polylines wrapped in curly braces.
// This is ~50-70% more compact than raw coordinates, allowing 2-3x more points.
const encodePolyline = (coords) => {
  let encoded = "";
  let prevLat = 0;
  let prevLng = 0;
  for (const [lng, lat] of coords) {
    const latE5 = Math.round(lat * 1e5);
    const lngE5 = Math.round(lng * 1e5);
    encoded += encodeValue(latE5 - prevLat);
    encoded += encodeValue(lngE5 - prevLng);
    prevLat = latE5;
    prevLng = lngE5;
  }
  return encoded;
};

const encodeValue = (value) => {
  let v = value < 0 ? ~(value << 1) : value << 1;
  let encoded = "";
  while (v >= 0x20) {
    encoded += String.fromCharCode((0x20 | (v & 0x1f)) + 63);
    v >>= 5;
  }
  encoded += String.fromCharCode(v + 63);
  return encoded;
};

// Perpendicular distance from point to line segment (for Douglas-Peucker)
const perpendicularDist = (point, lineStart, lineEnd) => {
  const dx = lineEnd[0] - lineStart[0];
  const dy = lineEnd[1] - lineStart[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    const ex = point[0] - lineStart[0];
    const ey = point[1] - lineStart[1];
    return Math.sqrt(ex * ex + ey * ey);
  }
  const t = Math.max(0, Math.min(1, ((point[0] - lineStart[0]) * dx + (point[1] - lineStart[1]) * dy) / lenSq));
  const projX = lineStart[0] + t * dx;
  const projY = lineStart[1] + t * dy;
  const ex = point[0] - projX;
  const ey = point[1] - projY;
  return Math.sqrt(ex * ex + ey * ey);
};

// Douglas-Peucker line simplification - preserves shape-defining points
const douglasPeucker = (coords, epsilon) => {
  if (coords.length <= 2) return coords;
  let maxDist = 0;
  let maxIdx = 0;
  const last = coords.length - 1;
  for (let i = 1; i < last; i++) {
    const d = perpendicularDist(coords[i], coords[0], coords[last]);
    if (d > maxDist) {
      maxDist = d;
      maxIdx = i;
    }
  }
  if (maxDist > epsilon) {
    const left = douglasPeucker(coords.slice(0, maxIdx + 1), epsilon);
    const right = douglasPeucker(coords.slice(maxIdx), epsilon);
    return left.slice(0, -1).concat(right);
  }
  return [coords[0], coords[last]];
};

// Simplify polygon using Douglas-Peucker, targeting a max point count
const simplifyPolygon = (coords, maxPoints = 100) => {
  if (coords.length <= maxPoints) return coords;
  let lo = 0;
  let hi = 0.01;
  let best = coords;
  for (let iter = 0; iter < 20; iter++) {
    const mid = (lo + hi) / 2;
    const result = douglasPeucker(coords, mid);
    if (result.length > maxPoints) {
      lo = mid;
    } else {
      hi = mid;
      best = result;
    }
  }
  // Ensure closed
  const first = best[0];
  const last = best[best.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    best.push(first);
  }
  return best;
};

// Create a circle polygon as an array of [lng, lat] coordinates
const createCirclePolygon = (centerLng, centerLat, radiusKm, steps = 64) => {
  const coords = [];
  const kmPerDegreeLat = 111.32;
  const kmPerDegreeLng = 111.32 * Math.cos((centerLat * Math.PI) / 180);

  for (let i = 0; i < steps; i++) {
    const angle = (i * 360) / steps;
    const angleRadians = (angle * Math.PI) / 180;
    const dx = radiusKm * Math.cos(angleRadians);
    const dy = radiusKm * Math.sin(angleRadians);
    const lng = centerLng + dx / kmPerDegreeLng;
    const lat = centerLat + dy / kmPerDegreeLat;
    coords.push([lng, lat]);
  }

  // Close the polygon
  if (coords.length > 0) {
    coords.push(coords[0]);
  }
  return coords;
};

// Build a Mapbox Static API path overlay using encoded polyline
const buildPathOverlay = (coords, options = {}) => {
  const {
    strokeWidth = 2,
    strokeColor = getTenantStaticMapColor(),
    strokeOpacity = 0.9,
    fillColor = getTenantStaticMapColor(),
    fillOpacity = 0.1,
  } = options;

  const polyline = encodePolyline(coords);
  const sOpacity = strokeOpacity < 1 ? strokeOpacity.toFixed(2) : "";
  const fOpacity = fillOpacity < 1 ? fillOpacity.toFixed(2) : "";

  return `path-${strokeWidth}+${strokeColor}${sOpacity ? `-${sOpacity}` : ""}+${fillColor}${fOpacity ? `-${fOpacity}` : ""}(${encodeURIComponent(polyline)})`;
};

// Pure function to compute Mapbox static URL from props - no side effects
const computeMapUrl = (center, zoom, radiusMiles, width, height, showPolygon, polygonCoordinates, padding) => {
  const accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
  if (!accessToken) return "";

  const numericCenter = Array.isArray(center)
    ? center.map((coord) => Number(coord))
    : [-123.2259181, 44.0431493];

  const [lng, lat] = numericCenter;

  // Clamp dimensions to Mapbox limits (1280x1280 max)
  const w = Math.min(Math.max(width, 1), 1280);
  const h = Math.min(Math.max(height, 1), 1280);

  try {
    if (showPolygon && polygonCoordinates?.length > 0) {
      // Polygon mode - draw zip code boundaries
      const allPolygonPaths = [];
      polygonCoordinates.forEach((polygon) => {
        if (!Array.isArray(polygon?.[0])) return;
        const coords = polygon[0]
          .map((point) => [Number(point.lng), Number(point.lat)])
          .filter(([pLng, pLat]) => !isNaN(pLng) && !isNaN(pLat) && (Math.abs(pLng) > 1 || Math.abs(pLat) > 1));
        if (coords.length > 0) {
          allPolygonPaths.push(coords);
        }
      });

      // If all polygon coordinates were invalid (e.g. {lat:0,lng:0}), fall through to radius/marker mode
      if (allPolygonPaths.length === 0) {
        const markerOverlay = `pin-l+${getTenantStaticMapColor()}(${lng},${lat})`;
        if (radiusMiles) {
          const radiusKm = Number(radiusMiles) * 1.60934 * 0.72;
          const circleCoords = createCirclePolygon(lng, lat, radiusKm);
          const circleOverlay = buildPathOverlay(circleCoords, {
            strokeWidth: 2, strokeColor: getTenantStaticMapColor(), strokeOpacity: 0.9,
            fillColor: getTenantStaticMapColor(), fillOpacity: 0.1,
          });
          const fbUrl = `${MAPBOX_BASE}/${markerOverlay},${circleOverlay}/auto/${w}x${h}@2x?access_token=${accessToken}&padding=${padding}&attribution=false`;
          return fbUrl.length > 8000
            ? `${MAPBOX_BASE}/${markerOverlay}/${lng},${lat},${zoom},0/${w}x${h}@2x?access_token=${accessToken}&attribution=false`
            : fbUrl;
        } else {
          return `${MAPBOX_BASE}/${markerOverlay}/${lng},${lat},${zoom},0/${w}x${h}@2x?access_token=${accessToken}&attribution=false`;
        }
      }

      // Try progressively tighter simplification tiers to stay under Mapbox's ~8192 URL-length limit before falling back to a marker
      const buildPolygonUrl = (budget) => {
        const parts = allPolygonPaths.map((polygonPath) => {
          const simplified = simplifyPolygon(polygonPath, budget);
          return buildPathOverlay(simplified, {
            strokeWidth: 2,
            strokeColor: getTenantStaticMapColor(),
            strokeOpacity: 0.9,
            fillColor: getTenantStaticMapColor(),
            fillOpacity: 0.2,
          });
        });
        return `${MAPBOX_BASE}/${parts.join(",")}/auto/${w}x${h}@2x?access_token=${accessToken}&padding=${padding}&attribution=false`;
      };

      const n = Math.max(allPolygonPaths.length, 1);
      const tier1 = Math.max(80, Math.floor(800 / n));
      const tier2 = Math.max(20, Math.floor(200 / n));
      const tier3 = Math.max(10, Math.floor(100 / n));

      const url1 = buildPolygonUrl(tier1);
      if (url1.length <= 8000) return url1;

      const url2 = buildPolygonUrl(tier2);
      if (url2.length <= 8000) return url2;

      const url3 = buildPolygonUrl(tier3);
      if (url3.length <= 8000) return url3;

      // Mapbox Static URL limit exceeded even at the tightest simplification - fall back to marker
      const markerOverlay = `pin-l+${getTenantStaticMapColor()}(${lng},${lat})`;
      return `${MAPBOX_BASE}/${markerOverlay}/${lng},${lat},${zoom},0/${w}x${h}@2x?access_token=${accessToken}&attribution=false`;
    } else if (radiusMiles) {
      // Radius circle mode - marker + circle overlay
      const markerOverlay = `pin-l+${getTenantStaticMapColor()}(${lng},${lat})`;
      const radiusKm = Number(radiusMiles) * 1.60934 * 0.72;
      const circleCoords = createCirclePolygon(lng, lat, radiusKm);
      const circleOverlay = buildPathOverlay(circleCoords, {
        strokeWidth: 2,
        strokeColor: getTenantStaticMapColor(),
        strokeOpacity: 0.9,
        fillColor: getTenantStaticMapColor(),
        fillOpacity: 0.1,
      });

      // Use auto positioning to fit the circle
      const url = `${MAPBOX_BASE}/${markerOverlay},${circleOverlay}/auto/${w}x${h}@2x?access_token=${accessToken}&padding=${padding}&attribution=false`;

      if (url.length > 8000) {
        return `${MAPBOX_BASE}/${markerOverlay}/${lng},${lat},${zoom},0/${w}x${h}@2x?access_token=${accessToken}&attribution=false`;
      } else {
        return url;
      }
    } else {
      // Default - just a marker at center
      const markerOverlay = `pin-s+${getTenantStaticMapColor()}(${lng},${lat})`;
      return `${MAPBOX_BASE}/${markerOverlay}/${lng},${lat},${zoom},0/${w}x${h}@2x?access_token=${accessToken}&attribution=false`;
    }
  } catch (error) {
    // Fallback to simple marker
    const markerOverlay = `pin-l+${getTenantStaticMapColor()}(${lng},${lat})`;
    return `${MAPBOX_BASE}/${markerOverlay}/${lng},${lat},${zoom},0/${w}x${h}@2x?access_token=${accessToken}&attribution=false`;
  }
};

const StaticMapboxComponent = ({
  center = [-112.074, 33.4484],
  zoom = 10,
  radiusMiles = 10,
  height = 400,
  width = 800,
  polygonCoordinates,
  showPolygon = false,
  isPolygonLoading = false,
  padding = 50,
}) => {
  // Compute URL synchronously during render - no useEffect flash
  const mapUrl = useMemo(
    () => computeMapUrl(center, zoom, radiusMiles, width, height, showPolygon, polygonCoordinates, padding),
    [center, zoom, radiusMiles, width, height, showPolygon, polygonCoordinates, padding],
  );

  // Don't render img until polygon data is ready
  const ready = !isPolygonLoading && mapUrl;

  return (
    <div
      style={{
        width: `100%`,
        height: `${height}px`,
        backgroundColor: "#f4f4f4",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {ready && (
        <img
          src={mapUrl}
          alt="Static map"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      )}
    </div>
  );
};

export default memo(StaticMapboxComponent);
